import React, { ReactNode, useState } from "react";
import { Flex, Grid, Input, Text } from "@chakra-ui/react";
import { Formik, FormikErrors } from "formik";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useGetBuyer from "@/shared/hooks/buyer/useGetBuyer";
import useGetBuyerBusinessLicense from "@/shared/hooks/business-license/useGetBuyerBusinessLicense";
import useUpdateBuyerBusinessLicense from "@/shared/hooks/business-license/useUpdateBuyerBusinessLicense";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useApplyPathPickMembership from "@/features/pathpick/features/membership/hooks/mutation/useApplyPathPickMembership";
import useGetPathPickMembershipPlans from "@/features/pathpick/features/membership/hooks/query/useGetPathPickMembershipPlans";
import PathPickMembershipBusinessInfoSection, {
  MembershipBusinessInfoFormValues,
} from "@/features/pathpick/features/membership/components/plan-change/PathPickMembershipBusinessInfoSection";
import {
  BUSINESS_REGISTRATION_NUMBER_PATTERN,
  formatBusinessRegistrationNumber,
} from "@/shared/constants/business-registration-certificate";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// 체험 조건 카드 3칸 — 요금제/기간/상품. 횟수(1회)는 하단 각주로만 안내한다.
const CONDITION_ROWS = ["plan", "period", "product"] as const;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputStyleProps = {
  fontSize: "13px",
  border: "0.5px solid #D4D4D1",
  borderRadius: "8px",
  background: "#FCFCFB",
  _placeholder: { color: "#8F8F8C" },
};

const FieldLabel = ({
  children,
  isRequired,
}: {
  children: ReactNode;
  isRequired?: boolean;
}) => (
  <Flex gap={"2px"} alignItems={"center"}>
    <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
      {children}
    </Text>
    {isRequired && (
      <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
        {"*"}
      </Text>
    )}
  </Flex>
);

// 저장된 사업자등록번호는 하이픈 유무가 응답마다 다를 수 있어 숫자만 비교한다.
const toDigits = (value: string | null | undefined) =>
  (value ?? "").replace(/[^0-9]/g, "");

/**
 * 무료체험 신청 모달(시안) — 체험 조건 + 사업자 정보 + 세금계산서 이메일.
 *
 * 무료체험도 신청이라 서버가 사업자등록증을 요구한다. 등록증이 없는 바이어가
 * 흐름을 떠나지 않도록, 유료 신청 모달과 같은 사업자 정보 섹션을 여기서도 연다
 * (파일 등록 → 자동 인식 → 번호·사업자명 확인). 입금이 없으므로 입금자명은 받지 않는다.
 */
function PathPickMembershipFreeTrialModal({ isOpen, onClose }: Props) {
  const { t } = useI18nHelper();
  const { info } = useAppToasts({ id: "GLOBAL" });
  const { buyer } = useGetBuyer();
  // 등록된 사업자등록증 — 폼 초기값과 신청 직전 저장 필요 여부의 기준.
  const { businessLicense } = useGetBuyerBusinessLicense();
  const { mutateAsync: updateBusinessLicense, isPending: isSavingBusinessInfo } =
    useUpdateBuyerBusinessLicense();
  const { findPlanIdByCode } = useGetPathPickMembershipPlans();
  const { mutateAsync: applyMembership, isPending: isApplying } =
    useApplyPathPickMembership();

  // 자동 인식이 끝나야 사업자 정보가 확정되므로 그동안은 시작 버튼을 잠근다.
  const [isCertificateParsing, setIsCertificateParsing] = useState(false);

  const freeTrialPlanId = findPlanIdByCode("FREE");

  const validate = (values: MembershipBusinessInfoFormValues) => {
    const errors: FormikErrors<MembershipBusinessInfoFormValues> = {};
    if (values.certificateFileId !== null) {
      if (
        !BUSINESS_REGISTRATION_NUMBER_PATTERN.test(
          values.businessRegistrationNumber.trim()
        )
      ) {
        errors.businessRegistrationNumber =
          "사업자등록번호 형식이 올바르지 않습니다. (예: 123-45-67890)";
      }
      if (!values.businessName.trim()) {
        errors.businessName = "사업자명을 입력해 주세요.";
      }
    }
    if (!EMAIL_REGEX.test(values.taxEmail.trim())) {
      errors.taxEmail = t("membership:change_modal.tax_email_error");
    }
    return errors;
  };

  // 파일을 새로 붙였거나 사업자 정보를 고친 경우에만 저장 API를 한 번 더 태운다.
  const hasBusinessInfoChanged = (values: MembershipBusinessInfoFormValues) =>
    values.certificateFileId !== (businessLicense?.id ?? null) ||
    toDigits(values.businessRegistrationNumber) !==
      toDigits(businessLicense?.businessRegistrationNumber) ||
    values.businessName.trim() !== (businessLicense?.businessName ?? "");

  const handleSubmit = async (values: MembershipBusinessInfoFormValues) => {
    if (freeTrialPlanId === undefined || values.certificateFileId === null) {
      return;
    }
    try {
      // 신청 바디에는 사업자등록번호·사업자명 필드가 없어, 먼저 사업자 정보로 저장한다.
      if (hasBusinessInfoChanged(values)) {
        await updateBusinessLicense({
          businessRegistrationCertificateFileId: values.certificateFileId,
          businessRegistrationNumber:
            values.businessRegistrationNumber.trim() || null,
          businessName: values.businessName.trim() || null,
        });
      }
      // 무료체험은 입금이 없어 서버가 즉시 승인·배정한다(주기는 서버가 월로 고정).
      await applyMembership({
        planId: freeTrialPlanId,
        taxInvoiceRequested: true,
        taxInvoiceEmail: values.taxEmail.trim(),
        businessRegistrationCertificateFileId: values.certificateFileId,
      });
      info(t("membership:free_trial_banner.success_toast"));
      onClose();
    } catch {
      // 실패 토스트는 각 mutation의 onError가 띄운다 — 모달은 열어 둔다.
    }
  };

  return (
    <Formik<MembershipBusinessInfoFormValues>
      enableReinitialize
      initialValues={{
        // 등록된 사업자등록증이 있으면 그 파일 id로 시작한다 (없으면 모달에서 등록해야 한다).
        certificateFileId: businessLicense?.id ?? null,
        businessRegistrationNumber: formatBusinessRegistrationNumber(
          businessLicense?.businessRegistrationNumber ?? ""
        ),
        businessName: businessLicense?.businessName ?? "",
        taxEmail: buyer?.taxInvoiceEmail ?? "",
      }}
      validate={validate}
      onSubmit={handleSubmit}
    >
      {({ values, errors, handleChange, handleSubmit: submitForm }) => (
        <CustomApprovalModal
          isOpen={isOpen}
          onClose={onClose}
          // 넘기지 않으면 기본값 480px이 그대로 걸려 375px 화면을 넘긴다.
          minWidth={"min(480px, 90dvw)"}
          isCentered={true}
        >
          <CustomApprovalModal.Header
            onClose={onClose}
            title={t("path_pick_guide:free_trial.modal.title")}
          />
          <CustomApprovalModal.Body>
            <Flex flexDirection={"column"} gap={"12px"}>
              <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
                {t("path_pick_guide:free_trial.modal.body")}
              </Text>
              <Grid
                gridTemplateColumns={"repeat(3, minmax(0, 1fr))"}
                gap={"8px"}
              >
                {CONDITION_ROWS.map((row) => (
                  <Flex
                    key={row}
                    flexDirection={"column"}
                    gap={"4px"}
                    alignItems={"center"}
                    padding={"12px 8px"}
                    borderRadius={"8px"}
                    border={"0.5px solid #E5E5E3"}
                    backgroundColor={"#F8F9FB"}
                  >
                    <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                      {t(`path_pick_guide:free_trial.modal.${row}_label`)}
                    </Text>
                    <Text textStyle={"body-sm-bold"} color={"#22221F"}>
                      {t(`path_pick_guide:free_trial.modal.${row}_value`)}
                    </Text>
                  </Flex>
                ))}
              </Grid>
              <Text textStyle={"caption-medium"} color={"#6F6F6C"}>
                {t("path_pick_guide:free_trial.modal.footnote")}
              </Text>

              {/* 등록된 사업자 정보 — 유료 신청 모달과 같은 섹션으로 여기서 등록한다 */}
              <Flex flexDirection={"column"} gap={"6px"}>
                <FieldLabel>
                  {t("membership:change_modal.business_info_title")}
                </FieldLabel>
                <PathPickMembershipBusinessInfoSection
                  onParsingChange={setIsCertificateParsing}
                />
              </Flex>

              {/* 세금계산서 — 발행 이메일은 항상 필수로 받는다(유료 신청과 동일) */}
              <Flex
                flexDirection={"column"}
                gap={"8px"}
                padding={"12px"}
                border={"0.5px solid #D4D4D1"}
                borderRadius={"8px"}
              >
                <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
                  {t("membership:change_modal.tax_invoice_section_title")}
                </Text>
                <Flex flexDirection={"column"} gap={"6px"}>
                  <FieldLabel isRequired>
                    {t("membership:change_modal.tax_email_label")}
                  </FieldLabel>
                  <Input
                    name={"taxEmail"}
                    value={values.taxEmail}
                    onChange={handleChange}
                    placeholder={t(
                      "membership:change_modal.tax_email_placeholder"
                    )}
                    {...inputStyleProps}
                  />
                  {errors.taxEmail && (
                    <Text textStyle={"caption-medium"} color={"#EE7D7A"}>
                      {errors.taxEmail}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </CustomApprovalModal.Body>
          <CustomApprovalModal.Footer
            cancelName={t("path_pick_guide:free_trial.modal.cancel")}
            confirmName={t("path_pick_guide:free_trial.modal.confirm")}
            onCancel={onClose}
            onConfirm={() => submitForm()}
            isLoading={isSavingBusinessInfo || isApplying}
            isConfirmDisabled={
              isCertificateParsing ||
              values.certificateFileId === null ||
              freeTrialPlanId === undefined ||
              Object.keys(errors).length > 0
            }
          />
        </CustomApprovalModal>
      )}
    </Formik>
  );
}

export default PathPickMembershipFreeTrialModal;
